import { Box, Typography, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { brand } from '../../theme/tokens';
import VersionToggle from './VersionToggle';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional leading icon rendered in a soft gold chip. */
  icon?: ReactNode;
  /** Primary action(s) shown on the right (e.g. a "Create" button). */
  action?: ReactNode;
  /** When set, renders a "Classic view" toggle linking to the old page. */
  classicPath?: string;
}

/**
 * Elegant, consistent page heading used across every revamped page.
 * Mobile-first: title block stacks above actions on small screens.
 */
const PageHeader = ({ title, subtitle, icon, action, classicPath }: PageHeaderProps) => (
  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              flexShrink: 0,
              width: 46,
              height: 46,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              color: 'primary.dark',
              background: brand.goldSoft,
              '& svg': { fontSize: 26 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" sx={{ lineHeight: 1.15 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {(action || classicPath) && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ width: { xs: '100%', sm: 'auto' }, '& > *': { flexShrink: 0 } }}
        >
          {classicPath && <VersionToggle to={classicPath} variant="classic" />}
          {action}
        </Stack>
      )}
    </Stack>
    <Box
      sx={{
        mt: 1.75,
        height: 3,
        width: 56,
        borderRadius: 999,
        background: `linear-gradient(90deg, ${brand.gold}, ${brand.goldLight})`,
      }}
    />
  </Box>
);

export default PageHeader;
